import { useState } from 'react';
import { Link } from 'react-router';
import { api, errorMessage } from '../../shared/api/client';
import { Alert, Button, Card, EmptyState, ErrorState, Field, Loading } from '../../shared/ui/components';
import { useAsync } from '../../shared/ui/useAsync';
import { useMe } from '../session/SessionProvider';
import { lessonKey, STAGES } from '../calling/content';

type Growth = { today: string; streak: number; readLast30: { day: string; read: boolean }[]; goals: { id: string; kind: string; title: string; target: number | null; dueOn: string | null; done: boolean; checkins: string[]; doneToday: boolean }[] };
type Route = { steps: { key: string; label: string; done: boolean; hint: string | null; link: string | null }[]; next: string | null };
const KINDS: [string, string][] = [['lectura', 'Leer la Biblia'], ['oracion', 'Orar'], ['raices', 'Raíces'], ['servir', 'Servir'], ['asistir', 'Asistir'], ['otra', 'Otra']];

/** Mi crecimiento: mi ruta, mi racha de lectura y mis metas. Todo privado, sin comparaciones. */
export function GrowthPage() {
  const me = useMe();
  const growth = useAsync((signal) => api.get<Growth>('/devotional/me/growth', signal), []);
  const route = useAsync((signal) => (me.activeChurchId ? api.get<Route>(`/reporting/churches/${me.activeChurchId}/me/route`, signal) : Promise.resolve(null)), [me.activeChurchId]);
  const [msg, setMsg] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const run = async (action: () => Promise<unknown>) => {
    setBusy(true);
    setMsg(null);
    try {
      await action();
      growth.reload();
      return true;
    } catch (e) {
      setMsg(errorMessage(e));
      return false;
    } finally {
      setBusy(false);
    }
  };
  return (
    <div className="page page--narrow">
      <header className="page__header">
        <h1>Mi crecimiento</h1>
        <p className="muted">Tu camino y tus metas. Solo tú lo ves; aquí nadie compite con nadie.</p>
      </header>
      {msg ? <Alert>{msg}</Alert> : null}
      <MyProgressCard readDays={growth.status === 'ready' ? growth.data.readLast30.filter((d) => d.read).length : null} />
      {route.status === 'ready' && route.data ? (
        <Card title="Mi ruta">
          <ol className="route">
            {route.data.steps.map((s) => (
              <li key={s.key} className={`route__step${s.done ? ' route__step--done' : ''}${route.data!.next === s.key ? ' route__step--next' : ''}`}>
                <strong>{s.done ? '✓ ' : ''}{s.label}</strong>
                {!s.done && route.data!.next === s.key && s.hint ? <span className="small">{s.hint} {s.link ? <Link to={s.link}>Ir</Link> : null}</span> : null}
              </li>
            ))}
          </ol>
        </Card>
      ) : null}
      {growth.status === 'loading' ? <Loading /> : null}
      {growth.status === 'error' ? <ErrorState error={growth.error} onRetry={growth.reload} /> : null}
      {growth.status === 'ready' ? (
        <>
          <Card title="Mi lectura">
            <p className="streak"><strong>{growth.data.streak}</strong> {growth.data.streak === 1 ? 'día seguido' : 'días seguidos'} leyendo</p>
            <div className="streak__days" aria-label="Últimos 30 días">
              {growth.data.readLast30.map((d) => <span key={d.day} className={d.read ? 'streak__day streak__day--on' : 'streak__day'} title={d.day} />)}
            </div>
            <p className="small muted">Cuenta lo que marcas en planes de lectura, lectura en grupo y campañas. <Link to="/app/devocional">Ir a planes</Link></p>
          </Card>
          <Card title="Mis metas">
            {growth.data.goals.length === 0 ? <EmptyState title="Ponte una meta: «Leer toda la semana», «Terminar Juan»…" /> : null}
            <ul className="list">
              {growth.data.goals.map((g) => (
                <li key={g.id} className="list__item list__item--stack">
                  <span>
                    <strong>{g.done ? '✓ ' : ''}{g.title}</strong>
                    <span className="small muted"> · {KINDS.find(([k]) => k === g.kind)?.[1]}{g.target ? ` · ${g.checkins.length} de ${g.target} días` : g.checkins.length ? ` · ${g.checkins.length} días` : ''}{g.dueOn ? ` · para el ${new Date(`${g.dueOn}T12:00:00`).toLocaleDateString('es', { day: 'numeric', month: 'short' })}` : ''}</span>
                  </span>
                  {g.target ? <progress className="usage" max={g.target} value={Math.min(g.checkins.length, g.target)} aria-label={`Avance de ${g.title}`} /> : null}
                  <div className="actions actions--wrap">
                    {!g.done ? (
                      <label className="check"><input type="checkbox" checked={g.doneToday} disabled={busy} onChange={(e) => void run(() => api.put(`/devotional/me/goals/${g.id}/today`, { on: e.target.checked }))} /> Hoy sí</label>
                    ) : null}
                    <Button variant="ghost" busy={busy} onClick={() => void run(() => api.put(`/devotional/me/goals/${g.id}/done`, { done: !g.done }))}>{g.done ? 'Reabrir' : 'Cumplida'}</Button>
                    <Button variant="ghost" busy={busy} onClick={() => { if (window.confirm(`¿Quitar la meta “${g.title}”?`)) void run(() => api.delete(`/devotional/me/goals/${g.id}`)); }}>Quitar</Button>
                  </div>
                </li>
              ))}
            </ul>
            <form className="form" onSubmit={(e) => {
              e.preventDefault();
              const form = e.currentTarget;
              const f = new FormData(form);
              const target = Number(f.get('target')) || null;
              void run(() => api.post('/devotional/me/goals', { kind: String(f.get('kind')), title: String(f.get('title') ?? '').trim(), target, dueOn: String(f.get('due') ?? '') || null })).then((ok) => ok && form.reset());
            }}>
              <div className="form__row">
                <div className="field"><label htmlFor="g-kind">Tipo</label><select id="g-kind" name="kind">{KINDS.map(([k, l]) => <option key={k} value={k}>{l}</option>)}</select></div>
                <Field label="Meta" name="title" required maxLength={120} placeholder="Leer la Biblia toda la semana" />
              </div>
              <div className="form__row">
                <Field label="Días (opcional)" name="target" type="number" min={1} max={366} />
                <Field label="Para cuándo (opcional)" name="due" type="date" />
              </div>
              <Button type="submit" busy={busy}>Agregar meta</Button>
            </form>
          </Card>
        </>
      ) : null}
    </div>
  );
}

/** «Mi avance»: lecciones de Quiero conocerle, versículos aprendidos y días de lectura, cada uno con su botón para seguir. */
function MyProgressCard({ readDays }: { readDays: number | null }) {
  const calling = useAsync((signal) => api.get<{ items: { lesson: string; doneAt: string | null }[] }>('/devotional/me/calling', signal).then((r) => r.items), []);
  const memory = useAsync((signal) => api.get<{ plans: { mastered: number }[] }>('/bible/me/memory', signal), []);
  const total = STAGES.reduce((n, st) => n + st.lessons.length, 0);
  const done = new Set(calling.status === 'ready' ? calling.data.filter((p) => p.doneAt).map((p) => p.lesson) : []);
  const lessons = STAGES.reduce((n, st) => n + st.lessons.filter((l) => done.has(lessonKey(st.key, l.key))).length, 0);
  const verses = memory.status === 'ready' ? memory.data.plans.reduce((n, p) => n + p.mastered, 0) : null;
  const rows: { label: string; value: string | null; to: string; action: string }[] = [
    { label: 'Quiero conocerle', value: calling.status === 'ready' ? `${lessons} de ${total} lecciones` : null, to: '/app/llamado', action: lessons >= total ? 'Repasar' : 'Seguir' },
    { label: 'Memorizar', value: verses === null ? null : `${verses} ${verses === 1 ? 'versículo aprendido' : 'versículos aprendidos'}`, to: '/app/biblia/memorizar', action: 'Repasar' },
    { label: 'Lectura', value: readDays === null ? null : `${readDays} ${readDays === 1 ? 'día' : 'días'} de lectura en los últimos 30`, to: '/app/devocional', action: 'Leer' },
  ];
  return (
    <Card title="Mi avance">
      <ul className="list">
        {rows.map((r) => (
          <li key={r.label} className="list__item">
            <span>
              <strong>{r.label}</strong>
              <span className="small muted block">{r.value ?? '…'}</span>
            </span>
            <Link className="btn btn--secondary" to={r.to} aria-label={`${r.action}: ${r.label}`}>
              <span>{r.action}</span>
            </Link>
          </li>
        ))}
      </ul>
    </Card>
  );
}
